import type { FilterSpecification, GeoJSONSource, Map as MaplibreMap, MapGeoJSONFeature, PointLike } from 'maplibre-gl'
import MapService from '@/services/map/MapService'
import { useMyMapStore } from '@/stores/myMapStore'

/**
 * Maximum zoom at which points are clustered. Above this, individual unclustered
 * circles are drawn. Must match `clusterMaxZoom` on the GeoJSON source.
 */
const CLUSTER_MAX_ZOOM = 14

/** Pixel radius within which nearby points merge into one cluster bubble. */
const CLUSTER_RADIUS_PX = 50

/** MapLibre layer types that represent point/POI features eligible for clustering. */
const POINT_LAYER_TYPES = new Set<string>(['symbol', 'circle'])

const CLUSTER_SOURCE_SUFFIX = '--cs'

const CLUSTER_LAYER_SUFFIX = {
  circles: '--cl-circles',
  count: '--cl-count',
  points: '--cl-points',
} as const

/** App primary teal used when the original layer has no resolvable solid color. */
const DEFAULT_CLUSTER_COLOR = '#025959'

/**
 * Several classification values selected at once in the right panel (e.g. "Publique" AND
 * "Privée"): each picto keeps its own category's color (see OsmLayerHandler.applyClassificationColor),
 * and cluster bubbles are colored by their dominant category via `clusterProperties` — see `setup`.
 */
export interface ClusterClassificationColoring {
  key: string
  values: Array<{ value: string; color: string }>
}

interface ClusterSetup {
  clusterSourceId: string
  /** Ordered [circles, count, unclustered-points] — reversed on removal. */
  clusterLayerIds: [string, string, string]
  /**
   * Original vector-tile point layer IDs rendered at opacity 0.
   * They stay on the map (NOT visibility:none) so MapLibre keeps fetching tiles
   * and querySourceFeatures can return features.
   */
  ghostLayerIds: string[]
  vectorSourceId: string
  sourceLayerName: string
  onMoveEnd: () => void
  onSourceData: (e: unknown) => void
  initialRefreshTimer: ReturnType<typeof setTimeout> | null
}

type SourceDataEvent = { isSourceLoaded?: boolean }

/**
 * Manages GeoJSON cluster layers on top of vector-tile OSM point/symbol layers.
 *
 * ── Why opacity-0 instead of visibility:none ──────────────────────────────────
 * MapLibre only fetches tiles for sources that have at least one **visible** layer.
 * Setting `visibility: 'none'` would stop tile loading → `querySourceFeatures`
 * returns nothing. Instead, the original point layers are kept alive at opacity 0
 * (invisible but still "active") so tiles continue to stream in as the user pans.
 *
 * ── Why circles for unclustered points ────────────────────────────────────────
 * The backend OSM style may reference sprite icons that are not in the map's loaded
 * sprite atlas. Using a circle avoids any icon/sprite dependency entirely.
 *
 * ── Flow ──────────────────────────────────────────────────────────────────────
 *   1. OsmLayerHandler calls `setup()` after `MartinStyleService.applyOsmStyleToMap`.
 *   2. Point/symbol layers are made opacity-0 (tiles keep loading).
 *   3. A GeoJSON cluster source + 3 layers (bubbles, count, points) are added.
 *   4. On `moveend` / `zoomend` / `sourcedata`, `querySourceFeatures` feeds the
 *      GeoJSON source with features from the loaded vector tile cache.
 *   5. OsmLayerHandler calls `remove()` before `removeOsmStyleFromMap`.
 *   6. On unmount, OsmLayerHandler calls `teardownAll()`.
 */
export class OsmClusterService {
  private static readonly registry = new Map<string, ClusterSetup>()

  // ─── ID helpers ──────────────────────────────────────────────────────────

  static clusterSourceId(styleId: string): string {
    return `${styleId}${CLUSTER_SOURCE_SUFFIX}`
  }

  static clusterCirclesLayerId(styleId: string): string {
    return `${styleId}${CLUSTER_LAYER_SUFFIX.circles}`
  }

  static clusterCountLayerId(styleId: string): string {
    return `${styleId}${CLUSTER_LAYER_SUFFIX.count}`
  }

  static clusterPointsLayerId(styleId: string): string {
    return `${styleId}${CLUSTER_LAYER_SUFFIX.points}`
  }

  /**
   * Returns the unclustered-points layer id that should be registered in
   * OsmLayerHandler's `activeOsmLayerIds` for hover/popup handling.
   */
  static getInteractiveLayerIds(styleId: string): string[] {
    if (!this.registry.has(styleId)) return []
    return [this.clusterPointsLayerId(styleId)]
  }

  /**
   * Forces a cluster data refresh for one already-set-up styleId. Called when the user (un)checks
   * a classification value in the right panel, since that filter is read fresh from the store
   * inside `refreshClusterData` but nothing otherwise re-triggers it outside of moveend/zoomend/
   * sourcedata (see the watcher in OsmLayerHandler.vue).
   */
  static refreshFilter(map: MaplibreMap, styleId: string): void {
    const entry = this.registry.get(styleId)
    if (!entry) return
    this.refreshClusterData(map, styleId, entry.vectorSourceId, entry.sourceLayerName, entry.clusterSourceId)
  }

  /**
   * Recolors the cluster bubble and the unclustered-points icon for an already-set-up styleId to
   * match the classification value currently selected in the right panel (or the type d'ouvrage's
   * own default icon/color when none is selected) — see OsmLayerHandler.applyClassificationColor,
   * which computes `imageId`/`color` and calls this right after updating the ghost layer itself.
   */
  static refreshIcon(map: MaplibreMap, styleId: string, imageId: string, color: string): void {
    const entry = this.registry.get(styleId)
    if (!entry) return
    const [circlesId, , pointsId] = entry.clusterLayerIds
    try {
      if (map.getLayer(circlesId)) map.setPaintProperty(circlesId, 'circle-color', color)
    } catch {
      // Gracefully handle any paint property errors
    }
    try {
      const pointsLayer = map.getLayer(pointsId)
      if (pointsLayer?.type === 'symbol') {
        map.setLayoutProperty(pointsId, 'icon-image', imageId)
      } else if (pointsLayer?.type === 'circle') {
        map.setPaintProperty(pointsId, 'circle-color', color)
      }
    } catch {
      // Gracefully handle any layout/paint property errors
    }
  }

  // ─── Setup ───────────────────────────────────────────────────────────────

  /**
   * Set up clustering after `MartinStyleService.applyOsmStyleToMap` has run.
   * Returns false when no point/symbol layers are found among `appliedLayerIds`.
   * `classificationColoring`, when several classification values are selected at once, makes
   * cluster bubbles reflect their dominant category color instead of one flat color — see
   * `buildDominantColorExpression`. Passing it requires `remove()` + `setup()` (not a plain
   * property update) because MapLibre GeoJSON sources can't have `clusterProperties` patched in
   * place once created.
   */
  static setup(
    map: MaplibreMap,
    styleId: string,
    appliedLayerIds: string[],
    classificationColoring?: ClusterClassificationColoring,
  ): boolean {
    const pointLayerIds = appliedLayerIds.filter((id) => {
      const layer = map.getLayer(id)
      return layer != null && POINT_LAYER_TYPES.has(layer.type)
    })
    if (pointLayerIds.length === 0) return false

    // `map.getLayer()` returns a StyleLayer whose source-layer property is `sourceLayer`
    // (camelCase internal field), matching the MapLibre v4 type declaration.
    const firstLayer = map.getLayer(pointLayerIds[0]) as
      | { type: string; source?: string; sourceLayer?: string }
      | undefined
    const vectorSourceId = firstLayer?.source
    if (!vectorSourceId) return false
    const sourceLayerName = firstLayer?.sourceLayer ?? ''

    const clusterColor = this.extractLayerColor(map, pointLayerIds[0]) ?? DEFAULT_CLUSTER_COLOR
    const circleColorPaint = (
      classificationColoring && classificationColoring.values.length > 0
        ? this.buildDominantColorExpression(classificationColoring, clusterColor)
        : clusterColor
    ) as import('maplibre-gl').DataDrivenPropertyValueSpecification<string>
    // Raw icon-image value from the original layer: may be a static string OR a MapLibre
    // expression (e.g. ['get', 'maki']). We pass it through as-is so the unclustered
    // layer can use the same icons without needing to know the expression shape.
    const iconImage = this.extractIconImageRaw(map, pointLayerIds[0])
    const iconSize = this.extractIconSize(map, pointLayerIds[0])

    // Text layout properties from the original symbol layer (configured in the admin
    // style editor). Carried into the unclustered points layer so labels appear.
    const textField = this.extractLayoutProp(map, pointLayerIds[0], 'text-field')
    const textSize = this.extractLayoutProp(map, pointLayerIds[0], 'text-size')
    const textAnchor = this.extractLayoutProp(map, pointLayerIds[0], 'text-anchor')
    const textOffset = this.extractLayoutProp(map, pointLayerIds[0], 'text-offset')
    const textFont = this.extractLayoutProp(map, pointLayerIds[0], 'text-font')
    const textColor = this.extractPaintProp(map, pointLayerIds[0], 'text-color')
    const textHaloColor = this.extractPaintProp(map, pointLayerIds[0], 'text-halo-color')
    const textHaloWidth = this.extractPaintProp(map, pointLayerIds[0], 'text-halo-width')

    const csId = this.clusterSourceId(styleId)

    // ── GeoJSON cluster source (starts empty) ────────────────────────────
    if (!map.getSource(csId)) {
      map.addSource(csId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS_PX,
        ...(classificationColoring && classificationColoring.values.length > 0
          ? { clusterProperties: this.buildClusterProperties(classificationColoring) }
          : {}),
      })
    }

    // ── Make original point layers invisible WITHOUT hiding them ──────────
    // visibility:none stops tile loading; opacity:0 keeps tiles streaming in.
    for (const id of pointLayerIds) {
      this.makeLayerOpacityZero(map, id)
    }

    const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
    const fondMeta = { isPersistent: true, layerGroup: MapService.LAYER_GROUP.FOND }

    const circlesId = this.clusterCirclesLayerId(styleId)
    const countId = this.clusterCountLayerId(styleId)
    const pointsId = this.clusterPointsLayerId(styleId)

    // ── Cluster bubble circles ────────────────────────────────────────────
    if (!map.getLayer(circlesId)) {
      map.addLayer(
        {
          id: circlesId,
          type: 'circle',
          source: csId,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': circleColorPaint,
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              18, // ≤ 9  → 18 px
              10,
              24, // ≤ 99 → 24 px
              100,
              30, // > 99 → 30 px
            ],
            'circle-opacity': 0.88,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': 'rgba(255,255,255,0.75)',
          },
          metadata: fondMeta,
        },
        beforeId,
      )
    }

    // ── Cluster count label ───────────────────────────────────────────────
    if (!map.getLayer(countId)) {
      map.addLayer(
        {
          id: countId,
          type: 'symbol',
          source: csId,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#ffffff' },
          metadata: fondMeta,
        },
        beforeId,
      )
    }

    // ── Unclustered individual points ─────────────────────────────────────
    // Use the original icon when available (sprite is already loaded by the basemap
    // or OSM style). Fall back to a circle only when the layer has no icon at all.
    if (!map.getLayer(pointsId)) {
      if (iconImage != null) {
        const textLayout: Record<string, unknown> = {
          'icon-image': iconImage as string,
          'icon-size': iconSize ?? 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        }
        if (textField != null) textLayout['text-field'] = textField
        if (textSize != null) textLayout['text-size'] = textSize
        if (textAnchor != null) textLayout['text-anchor'] = textAnchor
        if (textOffset != null) textLayout['text-offset'] = textOffset
        if (textFont != null) textLayout['text-font'] = textFont

        const textPaint: Record<string, unknown> = {}
        if (textColor != null) textPaint['text-color'] = textColor
        if (textHaloColor != null) textPaint['text-halo-color'] = textHaloColor
        if (textHaloWidth != null) textPaint['text-halo-width'] = textHaloWidth

        map.addLayer(
          {
            id: pointsId,
            type: 'symbol',
            source: csId,
            filter: ['!', ['has', 'point_count']],
            layout: textLayout as import('maplibre-gl').SymbolLayerSpecification['layout'],
            paint: textPaint as import('maplibre-gl').SymbolLayerSpecification['paint'],
            metadata: fondMeta,
          } as import('maplibre-gl').AddLayerObject,
          beforeId,
        )
      } else {
        map.addLayer(
          {
            id: pointsId,
            type: 'circle',
            source: csId,
            filter: ['!', ['has', 'point_count']],
            paint: {
              // Individual (unclustered) points, not aggregates: no `clusterProperties` to read a
              // dominant color from here — only relevant when this fallback (no icon at all) is
              // hit, which classification-enabled types never do since they always resolve to a
              // picto pin (see MartinStyleService.convertCirclePointLayersToPictoPins).
              'circle-color': clusterColor,
              'circle-radius': 7,
              'circle-opacity': 0.9,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': 'rgba(255,255,255,0.8)',
            },
            metadata: fondMeta,
          },
          beforeId,
        )
      }
    }

    // ── Event handlers ────────────────────────────────────────────────────
    const doRefresh = () => this.refreshClusterData(map, styleId, vectorSourceId, sourceLayerName, csId)

    const onMoveEnd = () => doRefresh()
    const onSourceData = (e: unknown) => {
      const ev = e as SourceDataEvent
      if (ev.isSourceLoaded) doRefresh()
    }

    map.on('moveend', onMoveEnd)
    map.on('zoomend', onMoveEnd)
    map.on('sourcedata', onSourceData as never)

    // Retry once tiles have had time to decode (initial querySourceFeatures may miss them)
    const initialRefreshTimer = setTimeout(() => doRefresh(), 1500)

    this.registry.set(styleId, {
      clusterSourceId: csId,
      clusterLayerIds: [circlesId, countId, pointsId],
      ghostLayerIds: pointLayerIds,
      vectorSourceId,
      sourceLayerName,
      onMoveEnd,
      onSourceData,
      initialRefreshTimer,
    })

    doRefresh()
    return true
  }

  // ─── Data refresh ─────────────────────────────────────────────────────────

  /**
   * Re-reads raw features from the vector-tile source and rebuilds the cluster GeoJSON source.
   * Applies the classification filter selected in the right panel (see MyMapOsmCategoryDetails)
   * directly to the `querySourceFeatures` call: unlike a layer `setFilter`, clustering reads raw
   * source features and ignores layer filters entirely, so without this the cluster/unclustered
   * points would keep showing every class even when only one is selected.
   */
  private static refreshClusterData(
    map: MaplibreMap,
    styleId: string,
    vectorSourceId: string,
    sourceLayerName: string,
    clusterSourceId: string,
  ): void {
    if (!map.getSource(vectorSourceId) || !map.getSource(clusterSourceId)) return

    try {
      const myMapStore = useMyMapStore()
      const classificationFilter = myMapStore.osmClassificationFilters.get(styleId)
      const filter = classificationFilter
        ? (['in', ['get', classificationFilter.key], ['literal', classificationFilter.values]] as unknown as FilterSpecification)
        : undefined
      const rawFeatures: MapGeoJSONFeature[] = map.querySourceFeatures(vectorSourceId, {
        sourceLayer: sourceLayerName,
        filter,
      })
      if (rawFeatures.length === 0) {
        // A filter that legitimately excludes every currently-loaded feature is a valid empty
        // result (e.g. only one class selected and no tiles for it in view yet) — clear the
        // cluster source instead of leaving stale features from before the filter was applied.
        const src = map.getSource(clusterSourceId) as GeoJSONSource | undefined
        if (filter) src?.setData({ type: 'FeatureCollection', features: [] })
        return
      }

      const seen = new Set<string>()
      const unique: GeoJSON.Feature[] = []

      for (const f of rawFeatures) {
        if (f.geometry.type !== 'Point') continue
        const coords = (f.geometry as GeoJSON.Point).coordinates
        const key = String(
          f.properties?.osm_id ?? f.id ?? `${coords[0].toFixed(5)},${coords[1].toFixed(5)}`,
        )
        if (seen.has(key)) continue
        seen.add(key)
        unique.push({ type: 'Feature', geometry: f.geometry, properties: f.properties ?? {} })
      }

      if (unique.length === 0) return

      const src = map.getSource(clusterSourceId) as GeoJSONSource | undefined
      src?.setData({ type: 'FeatureCollection', features: unique })
    } catch {
      // querySourceFeatures may throw briefly after source creation; safe to ignore
    }
  }

  // ─── Cluster click ────────────────────────────────────────────────────────

  /**
   * If the click lands on a cluster bubble, zoom to the cluster's expansion level.
   * Returns true when a cluster was hit (stops further click processing in OsmLayerHandler).
   */
  static handleClusterClick(
    map: MaplibreMap,
    point: PointLike,
    lngLat: { lng: number; lat: number },
  ): boolean {
    const circleLayerIds = [...this.registry.values()]
      .map((e) => e.clusterLayerIds[0])
      .filter((id) => map.getLayer(id))

    if (circleLayerIds.length === 0) return false

    const hit = map.queryRenderedFeatures(point, { layers: circleLayerIds })
    const clusterFeature = hit.find((f) => f.properties?.cluster === true)
    if (!clusterFeature) return false

    const clusterId = clusterFeature.properties?.cluster_id as number | undefined
    const sourceId = clusterFeature.layer.source as string
    const src = map.getSource(sourceId) as GeoJSONSource | undefined
    if (!src || clusterId == null) return false

    void src.getClusterExpansionZoom(clusterId).then((zoom) => {
      map.easeTo({ center: [lngLat.lng, lngLat.lat], zoom: zoom + 0.5 })
    })

    return true
  }

  // ─── Removal ─────────────────────────────────────────────────────────────

  /**
   * Remove cluster layers/source for the given styleId.
   * Ghost (opacity-0) vector-tile layers are left for `removeOsmStyleFromMap` to clean up.
   * Must be called BEFORE `MartinStyleService.removeOsmStyleFromMap`.
   */
  static remove(map: MaplibreMap, styleId: string): void {
    const entry = this.registry.get(styleId)
    if (!entry) return

    if (entry.initialRefreshTimer != null) clearTimeout(entry.initialRefreshTimer)
    map.off('moveend', entry.onMoveEnd)
    map.off('zoomend', entry.onMoveEnd)
    map.off('sourcedata', entry.onSourceData as never)

    for (const id of [...entry.clusterLayerIds].reverse()) {
      if (map.getLayer(id)) map.removeLayer(id)
    }
    if (map.getSource(entry.clusterSourceId)) {
      map.removeSource(entry.clusterSourceId)
    }

    this.registry.delete(styleId)
  }

  /** Remove all active cluster setups. Call on component unmount. */
  static teardownAll(map: MaplibreMap): void {
    for (const [styleId] of [...this.registry]) {
      this.remove(map, styleId)
    }
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /** Cluster-aggregate property name counting how many of a cluster's points carry this value. */
  private static clusterCountPropName(value: string): string {
    return `cls__${value}`
  }

  /**
   * `clusterProperties` for a GeoJSON cluster source: one summed counter per selected
   * classification value, so each cluster bubble knows how many of its points belong to each
   * category — read back by `buildDominantColorExpression` to pick the bubble's color.
   */
  private static buildClusterProperties(
    coloring: ClusterClassificationColoring,
  ): Record<string, [string, unknown]> {
    const properties: Record<string, [string, unknown]> = {}
    for (const value of coloring.values) {
      properties[this.clusterCountPropName(value.value)] = [
        '+',
        ['case', ['==', ['get', coloring.key], value.value], 1, 0],
      ]
    }
    return properties
  }

  /**
   * Paint expression picking each cluster bubble's color from its dominant classification value
   * (the selected category with the most points inside that bubble; ties resolve to whichever
   * value was selected first). Falls back to `fallbackColor` when a bubble has none of the
   * selected values (shouldn't happen since the source is already filtered to them).
   */
  private static buildDominantColorExpression(
    coloring: ClusterClassificationColoring,
    fallbackColor: string,
  ): unknown {
    const values = coloring.values
    const expr: unknown[] = ['case']
    for (let i = 0; i < values.length; i++) {
      const propName = this.clusterCountPropName(values[i].value)
      let condition: unknown = true
      for (let j = 0; j < values.length; j++) {
        if (i === j) continue
        const otherPropName = this.clusterCountPropName(values[j].value)
        const comparison = ['>=', ['get', propName], ['get', otherPropName]]
        condition = condition === true ? comparison : ['all', condition, comparison]
      }
      expr.push(condition, values[i].color)
    }
    expr.push(fallbackColor)
    return expr
  }

  /**
   * Sets a layer's rendered opacity to 0 WITHOUT using visibility:none.
   * visibility:none stops MapLibre from requesting tiles for the source;
   * opacity:0 keeps the layer "active" so tiles continue to load and
   * querySourceFeatures can return results.
   */
  private static makeLayerOpacityZero(map: MaplibreMap, layerId: string): void {
    const layer = map.getLayer(layerId)
    if (!layer) return
    try {
      if (layer.type === 'circle') {
        map.setPaintProperty(layerId, 'circle-opacity', 0)
        map.setPaintProperty(layerId, 'circle-stroke-opacity', 0)
      } else if (layer.type === 'symbol') {
        map.setPaintProperty(layerId, 'icon-opacity', 0)
        map.setPaintProperty(layerId, 'text-opacity', 0)
      } else if (layer.type === 'fill') {
        map.setPaintProperty(layerId, 'fill-opacity', 0)
      } else if (layer.type === 'line') {
        map.setPaintProperty(layerId, 'line-opacity', 0)
      }
    } catch {
      // Gracefully handle any paint property errors
    }
  }

  private static extractLayerColor(map: MaplibreMap, layerId: string): string | undefined {
    try {
      const layer = map.getLayer(layerId)
      if (!layer) return undefined
      let color: unknown
      if (layer.type === 'circle') color = map.getPaintProperty(layerId, 'circle-color')
      else if (layer.type === 'fill') color = map.getPaintProperty(layerId, 'fill-color')
      else if (layer.type === 'line') color = map.getPaintProperty(layerId, 'line-color')
      else if (layer.type === 'symbol') {
        // Picto pin layers (see MartinStyleService.convertCirclePointLayersToPictoPins) encode their
        // color in the icon id so cluster bubbles can still match it: plain pins use
        // `osm-picto-pin-{hex}`, marker_config-driven pins use `osm-picto-pin-mc-{hex}-{hash}`
        // (see TypeOuvrageMarkerService.registerMarkerConfigImage) — both share the same hex-after-prefix shape.
        const iconImage = map.getLayoutProperty(layerId, 'icon-image')
        const match =
          typeof iconImage === 'string' ? iconImage.match(/^osm-picto-pin(?:-mc)?-([0-9a-fA-F]{3,8})(?:-|$)/) : null
        if (match) color = `#${match[1]}`
      }
      return typeof color === 'string' ? color : undefined
    } catch {
      return undefined
    }
  }

  /**
   * Returns the raw `icon-image` layout value — which may be a static string OR a
   * MapLibre expression array (e.g. `['get', 'maki']`). Both are valid as the
   * `icon-image` property of a new symbol layer, so we pass them through as-is.
   * Returns null when the layer is not a symbol or has no icon defined.
   */
  private static extractIconImageRaw(map: MaplibreMap, layerId: string): unknown {
    try {
      const layer = map.getLayer(layerId)
      if (layer?.type !== 'symbol') return null
      const icon = map.getLayoutProperty(layerId, 'icon-image')
      return icon ?? null
    } catch {
      return null
    }
  }

  /** Returns the `icon-size` layout value, or undefined when not set. */
  private static extractIconSize(map: MaplibreMap, layerId: string): number | undefined {
    try {
      const layer = map.getLayer(layerId)
      if (layer?.type !== 'symbol') return undefined
      const size = map.getLayoutProperty(layerId, 'icon-size')
      return typeof size === 'number' ? size : undefined
    } catch {
      return undefined
    }
  }

  /** Returns any layout property value for the given layer, or null when absent. */
  private static extractLayoutProp(map: MaplibreMap, layerId: string, property: string): unknown {
    try {
      const value = map.getLayoutProperty(layerId, property)
      return value ?? null
    } catch {
      return null
    }
  }

  /** Returns any paint property value for the given layer, or null when absent. */
  private static extractPaintProp(map: MaplibreMap, layerId: string, property: string): unknown {
    try {
      const value = map.getPaintProperty(layerId, property)
      return value ?? null
    } catch {
      return null
    }
  }
}
